import { phoneForSave } from './phoneSave';

// BL-177 PR C: a person's phones, emails and addresses are LABELED entries (Mobile / Work / Home / Other), exactly one Primary per
// kind. They live in contact_methods; contacts.phones / emails / addresses stay as a primary-first flat mirror the database keeps in
// step (so everything that matches on "any phone" keeps working). These helpers turn that into editable rows and back into writes.

export const METHOD_LABELS = [
    { value: 'mobile', label: 'Mobile' },
    { value: 'work', label: 'Work' },
    { value: 'home', label: 'Home' },
    { value: 'other', label: 'Other' },
];
export const METHOD_KINDS = ['phone', 'email', 'address'];

const LABEL_VALUES = new Set(METHOD_LABELS.map(l => l.value));
export const normalizeLabel = (label, fallback = 'other') => {
    const text = typeof label === 'string' ? label.trim().toLowerCase() : '';
    return LABEL_VALUES.has(text) ? text : fallback;
};

export const methodLabelText = (label) => METHOD_LABELS.find(l => l.value === label)?.label || 'Other';

export const emptyEntry = (kind, label) => ({
    id: null,
    label: label || (kind === 'phone' ? 'mobile' : kind === 'email' ? 'work' : 'home'),
    value: '',
    is_primary: false,
});

const byPrimaryThenOrder = (a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0);

/**
 * Editable rows for one kind. Uses the person's contact_methods when loaded; otherwise falls back to the flat arrays on the contact
 * (first value = Primary), so a card still shows every number before the entries are fetched. Always returns at least one row.
 */
export function entriesOf(contact, kind) {
    const methods = (contact?.contact_methods || []).filter(m => m.kind === kind);
    let rows;
    if (methods.length) {
        rows = methods.map(m => ({ id: m.id, label: normalizeLabel(m.label), value: m.value || '', is_primary: !!m.is_primary }))
            .sort(byPrimaryThenOrder);
    } else {
        const legacy = kind === 'phone' ? contact?.phones : kind === 'email' ? contact?.emails : [];
        rows = (Array.isArray(legacy) ? legacy : [])
            .map(v => (typeof v === 'string' ? v : v?.number || v?.phone || v?.value || v?.phone_number || ''))
            .filter(Boolean)
            .map((value, index) => ({ id: null, label: 'other', value, is_primary: index === 0 }));
    }
    return rows.length ? rows : [{ ...emptyEntry(kind), is_primary: true }];
}

/** What gets stored for one entry value. */
export const entryValueForSave = (kind, value) => {
    const text = typeof value === 'string' ? value.trim() : '';
    if (!text) return '';
    if (kind === 'phone') return phoneForSave(text);
    if (kind === 'email') return text;
    return text;
};

/** Blank rows dropped, duplicates collapsed, exactly one Primary (the first marked, else the first row). */
export function cleanEntries(entries, kind) {
    const seen = new Set();
    const kept = [];
    for (const entry of entries || []) {
        const value = entryValueForSave(kind, entry.value);
        if (!value) continue;
        const key = kind === 'email' || kind === 'address' ? value.toLowerCase() : value;
        if (seen.has(key)) continue;
        seen.add(key);
        kept.push({ ...entry, value, label: normalizeLabel(entry.label) });
    }
    if (!kept.length) return [];
    const primaryIndex = Math.max(0, kept.findIndex(e => e.is_primary));
    return kept.map((e, index) => ({ ...e, is_primary: index === primaryIndex }));
}

export const primaryEntry = (entries) => (entries || []).find(e => e.is_primary && (e.value || '').trim()) || (entries || []).find(e => (e.value || '').trim()) || null;

/** The writes needed to turn `original` rows (as loaded) into `next` rows (as edited). */
export function diffEntries(original, next, kind) {
    const before = (original || []).filter(e => e.id);
    const after = cleanEntries(next, kind);
    // A value already on file (even one shown from the flat arrays before its entry was loaded) is never inserted twice.
    const norm = (value) => (kind === 'phone' ? value : value.toLowerCase());
    const onFile = new Set((original || []).map(e => entryValueForSave(kind, e.value)).filter(Boolean).map(norm));
    const afterById = new Map(after.filter(e => e.id).map(e => [e.id, e]));
    const deletes = before.filter(e => !afterById.has(e.id)).map(e => e.id);
    const inserts = after.filter(e => !e.id && !onFile.has(norm(e.value)));
    const updates = [];
    for (const e of before) {
        const now = afterById.get(e.id);
        if (!now) continue;
        if (now.value !== e.value || now.label !== e.label) updates.push({ id: e.id, value: now.value, label: now.label });
    }
    const wasPrimary = before.find(e => e.is_primary)?.id || null;
    const nowPrimary = after.find(e => e.is_primary) || null;
    // a Primary that is a brand-new row is made Primary by the insert; an existing row is promoted by an update
    const promote = nowPrimary?.id && nowPrimary.id !== wasPrimary ? nowPrimary.id : null;
    return { inserts, updates, deletes, promote, primaryIsNew: Boolean(nowPrimary && !nowPrimary.id) };
}

/** Apply the diff (deletes, then edits, then inserts, then the Primary). Admin-only on the database side (RLS). */
export async function saveContactMethods(client, { contactId, kind, original, next, tableName = 'contact_methods' }) {
    const plan = diffEntries(original, next, kind);
    const table = () => client.from(tableName);   // PR D reuses this for personal_contact_methods
    if (plan.deletes.length) {
        const { error } = await table().delete().in('id', plan.deletes);
        if (error) throw error;
    }
    for (const update of plan.updates) {
        const { error } = await table().update({ value: update.value, label: update.label }).eq('id', update.id);
        if (error) throw error;
    }
    for (const entry of plan.inserts) {
        const { error } = await table().insert([{
            contact_id: contactId, kind, label: entry.label, value: entry.value, is_primary: entry.is_primary,
        }]);
        if (error) throw error;
    }
    if (plan.promote) {
        const { error } = await table().update({ is_primary: true }).eq('id', plan.promote);
        if (error) throw error;
    }
    return plan;
}

/** Same plan, for people who cannot edit existing entries but just added the person: only inserts. */
export const hasMethodChanges = (original, next, kind) => {
    const plan = diffEntries(original, next, kind);
    return plan.inserts.length + plan.updates.length + plan.deletes.length > 0 || Boolean(plan.promote);
};
